import { Autocomplete, Box, Button, TextField, Typography } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useEffect, useId, useMemo, useState } from "react";

interface GroupedOption {
  label: string;
  value: any;
  name?: string;
  id?: any;
  group?: string;
}

interface SelectGroupProps {
  data: GroupedOption[];
  error?: string;
  onChange?: (value: string | null) => void;
  value?: any;
  placeholder?: string;
  width?: number | string;
  height?: number;
  disabled?: boolean;
  onCreate?: (name: string) => void;
  createLabel?: (name: string) => string;
}

const CREATE_ACTION = '__create__';

const SelectGroup = ({
    data = [],
    error,
    onChange,
    value,
    placeholder,
    width = 300,
    height = 55,
    disabled = false,
    onCreate,
    createLabel,
}: SelectGroupProps) => {
    const uniqueId = useId();
    const [inputValue, setInputValue] = useState('');

    const getOptionLabel = (option: any) => option?.label || option?.name || '';
    const getOptionId = (option: any) => option?.id ?? option?.value ?? null;

    const selectedOption = useMemo(() => {
        if (!value) return null;
        if (typeof value === 'object') {
            const id = value.id ?? value.value;
            return data.find((item) => getOptionId(item) === id) || null;
        }
        return data.find((item) => String(getOptionId(item)) === String(value)) || null;
    }, [value, data]);

    useEffect(() => {
        if (selectedOption) {
            setInputValue(getOptionLabel(selectedOption));
        } else if (!value) {
            setInputValue('');
        }
    }, [selectedOption, value]);

    // Garante que opções com group apareçam agrupadas e as sem grupo no final ("Outros")
    const sortedOptions = useMemo(() => {
        return [...data].sort((a, b) => {
            const groupA = a.group || 'Outros';
            const groupB = b.group || 'Outros';
            if (groupA === groupB) return getOptionLabel(a).localeCompare(getOptionLabel(b), 'pt-BR');
            return groupA.localeCompare(groupB, 'pt-BR');
        });
    }, [data]);

    const filterOptions = (options: any[], state: { inputValue: string }) => {
        const normalized = state.inputValue.trim().toLowerCase();
        const filtered = normalized
            ? options.filter((opt) => getOptionLabel(opt).toLowerCase().includes(normalized))
            : options;

        if (onCreate && !disabled && normalized && filtered.length === 0) {
            return [
                {
                    id: CREATE_ACTION,
                    value: CREATE_ACTION,
                    label: state.inputValue.trim(),
                    actionType: 'create',
                    group: '',
                },
            ];
        }

        return filtered;
    };

    return (
        <Box sx={{ width }} key={`select-group-${uniqueId}`}>
            <Autocomplete
                disablePortal
                options={sortedOptions}
                value={selectedOption}
                inputValue={inputValue}
                disabled={disabled}
                groupBy={(option) => option.group || 'Outros'}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={(option, val) => {
                    if (!option || !val) return false;
                    if (option.actionType || val.actionType) return false;
                    return getOptionId(option) === getOptionId(val);
                }}
                filterOptions={filterOptions}
                onInputChange={(_, newInput, reason) => {
                    if (reason === 'reset') {
                        setInputValue(selectedOption ? getOptionLabel(selectedOption) : '');
                        return;
                    }
                    setInputValue(newInput);
                }}
                onChange={(_, newValue) => {
                    if (newValue?.actionType === 'create') {
                        onCreate?.(newValue.label);
                        setInputValue('');
                        return;
                    }

                    if (!newValue) {
                        onChange?.(null);
                        return;
                    }

                    const id = getOptionId(newValue);
                    onChange?.(id != null ? String(id) : null);
                }}
                sx={{
                    width: '100%',
                    backgroundColor: '#fff',
                    '& .MuiInputBase-root': { minHeight: height },
                }}
                renderOption={(props, option) => {
                    const { key, ...rest } = props as any;

                    if (option.actionType === 'create') {
                        return (
                            <Box component="li" key={key} {...rest}>
                                <Button
                                    fullWidth
                                    startIcon={<AddCircleOutlineIcon />}
                                    sx={{ justifyContent: 'flex-start', textTransform: 'none', px: 0.5 }}
                                >
                                    {createLabel ? createLabel(option.label) : `Adicionar "${option.label}"`}
                                </Button>
                            </Box>
                        );
                    }

                    return (
                        <Box component="li" key={key} {...rest}>
                            <Typography variant="body2">{getOptionLabel(option)}</Typography>
                        </Box>
                    );
                }}
                renderGroup={(params) => (
                    <li key={params.key}>
                        <Box
                            sx={{
                                fontWeight: 'bold',
                                backgroundColor: 'rgba(77, 176, 177, 0.15)',
                                color: '#4db0b1',
                                fontSize: '0.875rem',
                                lineHeight: '36px',
                                paddingLeft: '16px',
                                borderTop: '1px solid rgba(77, 176, 177, 0.3)',
                                borderBottom: '1px solid rgba(77, 176, 177, 0.3)',
                            }}
                        >
                            {params.group}
                        </Box>
                        <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={placeholder}
                        error={!!error}
                        className={`container-input__input ${error ? 'container-input__input__error' : 'container-input__input__default'}`}
                    />
                )}
            />
            {error && (
                <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {error.toString()}
                </span>
            )}
        </Box>
    );
};

export default SelectGroup;
